import { put, race, select, take, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import {
  BusinessAction,
  SaleAction,
  closeDispatchingModal,
  updateModalContext,
} from "actions";
import { addConsignment } from "actions/offline/consignments";
import { addSaleLot } from "actions/offline/saleLots";
import { getConsignmentPayload } from "lib/consignments";
import { SALE } from "constants/actionTypes";
import { UNKNOWN_BUSINESS_NAME } from "constants/businesses";
import { DeploymentPermission } from "constants/permissions";
import { ModalTypes } from "constants/navigation";
import { ScreenType } from "constants/screen";
import { SaleType } from "constants/sale";
import { SexName } from "constants/sexes";
import {
  getActiveLivestockAgentDeployment,
  getDefaultBillingSettings,
  getScreenType,
  getSexes,
} from "selectors";
import { selectUnknownBusinessIds } from "selectors/businesses";

import history from "appHistory";
import { getDefaultTransitInsuranceValues } from "lib/insuranceRates";
import { getSaleEntryRoute } from "lib/navigation";
import { hasPermission } from "lib/permissions";
import { EMPTY_ARRAY } from "lib";
import { calculateTotalPriceCents } from "lib/pricing";

import { PricingTypeId } from "constants/pricingTypes";
import { MobileTransactionalCreateSaleFormValues } from "components/MobileTransactionalModal";

function* createMobileTransactionalSale(action: {
  type: string;
  values: MobileTransactionalCreateSaleFormValues;
}) {
  const { values } = action;

  yield put(
    updateModalContext(ModalTypes.MobileTransactionalCreateSaleModal, {
      isContextSubmitting: true,
    }),
  );

  const state = yield select();

  const isMobile = getScreenType(state) !== ScreenType.DESKTOP;

  const activeDeployment = getActiveLivestockAgentDeployment(state);

  const hasAlternativeWorkflowPermission = hasPermission(
    activeDeployment,
    DeploymentPermission.featureAlternativeMobileWorkflow,
  );

  const { sale, consignment, saleLot, saleLots = [], skinsLots } = values;
  const saleType = sale.sale_type;
  const isHooks = saleType === SaleType.OVER_HOOKS;
  const isPaddock = saleType === SaleType.PADDOCK;

  const processedSkinsLots = isHooks
    ? skinsLots
        .filter(skinsLot => skinsLot.quantity)
        .map(skinsLot => ({
          quantity: skinsLot.quantity,
          total_price_cents: calculateTotalPriceCents(skinsLot),
        }))
    : EMPTY_ARRAY;

  // 1. Create the sale via redux-offline, tagged so onSaleCreateSuccess skips navigation
  // Inherit the sale lot's pricing type onto the sale.
  // For hooks/paddock sales, pre-set the default buyer so the sale entry screen
  // pre-populates correctly. For hooks sales, also set the default vendor.
  const salePayload = {
    ...sale,
    pricing_type_id: saleLots[0]?.pricing_type_id ?? null,
    default_buyer_id:
      (isHooks || isPaddock) && saleLot.buyer_id ? saleLot.buyer_id : null,
    default_vendor_id:
      (isHooks || isPaddock) && consignment.vendor_id
        ? consignment.vendor_id
        : null,
  };
  yield put(SaleAction.create(salePayload, { skipNavigation: true }));

  const { success, failure } = yield race({
    success: take(
      (a: { type: string; options?: { skipNavigation?: boolean } }) =>
        a.type === SALE.CREATE.SUCCESS && a.options?.skipNavigation,
    ),
    failure: take(
      (a: { type: string; options?: { skipNavigation?: boolean } }) =>
        a.type === SALE.CREATE.FAILURE && a.options?.skipNavigation,
    ),
  });

  if (failure) {
    yield put(
      updateModalContext(ModalTypes.MobileTransactionalCreateSaleModal, {
        isContextSubmitting: false,
      }),
    );
    return;
  }

  const {
    livestocksale_id: livestockSaleId,
    saleyard_name: saleyardName,
    sale_type: payloadSaleType,
    rounds,
  } = success.payload;

  const saleRoundId = rounds?.[0] || null;

  // 2. Queue consignment — uses temp UUID; processed in order by the offline queue
  // if there is no vendor id passed in, create an unknown vendor to assign to.
  let vendorId = consignment.vendor_id;
  if (!vendorId) {
    const unknownBusinessIds: string[] = yield select(selectUnknownBusinessIds);
    if (unknownBusinessIds.length > 0) {
      vendorId = unknownBusinessIds[0];
    } else {
      const billingSettings = yield select(getDefaultBillingSettings);
      vendorId = uuidv4();
      yield put(
        BusinessAction.create(
          {
            id: vendorId,
            name: UNKNOWN_BUSINESS_NAME,
            ...getDefaultTransitInsuranceValues(billingSettings),
            isTemporary: true,
            addedInLivestockSaleId: livestockSaleId,
          },
          { changeReason: "Created as the unknown vendor for a consignment." },
        ),
      );
    }
  }

  const consignmentTempId = uuidv4();
  const totalQuantity = saleLots.reduce(
    (sum, lot) => sum + (Number(lot.quantity) || 0),
    0,
  );

  yield put(
    addConsignment(
      consignmentTempId,
      getConsignmentPayload({
        ...consignment,
        vendor_id: vendorId,
        vendor_property_id: consignment.vendor_property_id,
        quantity: totalQuantity,
      }),
      { livestocksale_id: livestockSaleId, saleyard_name: saleyardName },
    ),
  );

  const sexes: Record<
    string,
    { species_id: number; name: string; id: string; hasProgeny?: boolean }
  > = yield select(getSexes);

  // 3. Queue a sale lot for each entry in saleLots
  for (const lot of saleLots) {
    const lotTotalPriceCents = calculateTotalPriceCents({
      unitPrice: Number(lot.unitPrice) || 0,
      quantity: lot.quantity || 0,
      pricing_type_id: lot.pricing_type_id,
      total_mass_grams: Number(lot.total_mass_grams) || 0,
    });

    const selectedSexHasProgeny = Boolean(sexes[lot.sex_id]?.hasProgeny);
    const lotTempId = uuidv4();

    yield put(
      addSaleLot(
        {
          ...lot,
          buyer_id: saleLot.buyer_id,
          total_mass_grams: Math.round(Number(lot.total_mass_grams) || 0),
          quantity_progeny: selectedSexHasProgeny ? lot.quantity_progeny : 0,
          sale_round_id: saleRoundId,
          consignment_id: consignmentTempId,
          livestocksale_id: livestockSaleId,
          total_price_cents: lotTotalPriceCents,
          destination_property_id: saleLot.destination_property_id,
        },
        lotTempId,
      ),
    );
  }

  // 4. Queue any skin lots as additional sale lots on the same consignment
  if (processedSkinsLots.length) {
    const skinsSexId = Object.values(sexes).find(
      sex => sex.species_id === sale.species_id && sex.name === SexName.SKINS,
    )?.id;

    for (const skinsLot of processedSkinsLots) {
      yield put(
        addSaleLot(
          {
            consignment_id: consignmentTempId,
            livestocksale_id: livestockSaleId,
            quantity: skinsLot.quantity,
            total_price_cents: skinsLot.total_price_cents,
            pricing_type_id: PricingTypeId.PER_HEAD,
            sale_round_id: saleRoundId,
            sex_id: skinsSexId,
            buyer_id: saleLot.buyer_id,
            destination_property_id: saleLot.destination_property_id,
          },
          uuidv4(),
        ),
      );
    }
  }

  const resolvedSaleType = saleType || payloadSaleType;

  const route =
    isMobile && hasAlternativeWorkflowPermission ? "sale-lot-cards" : null;

  history.push(
    getSaleEntryRoute(saleyardName, livestockSaleId, resolvedSaleType, route),
  );
  yield put(
    closeDispatchingModal(ModalTypes.MobileTransactionalCreateSaleModal),
  );
}

export default function* mobileTransactionalSaleSaga() {
  yield takeEvery(
    SALE.CREATE_MOBILE_TRANSACTIONAL_SALE.ACTION,
    createMobileTransactionalSale,
  );
}
